import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiUrl } from '../../config/api';
import './websiteChat.css';

const DAYS = [
  ['1', 'Lunes'], ['2', 'Martes'], ['3', 'Miércoles'], ['4', 'Jueves'], ['5', 'Viernes'], ['6', 'Sábado'], ['0', 'Domingo'],
];

const EMPTY_REPLY = { id: null, title: '', bodyText: '', isActive: true, sortOrder: 0 };
const DEFAULT_WELCOME_REPLY = {
  id: 'default-welcome',
  title: 'Bienvenida predeterminada',
  bodyText: '¡Hola! Escríbenos y nuestro equipo te responderá por aquí.',
};
const DEFAULT_AFTER_HOURS_REPLY = {
  id: 'default-after-hours',
  title: 'Fuera de horario predeterminado',
  bodyText: 'En este momento estamos fuera del horario de atención. Déjanos tu mensaje y responderemos al comenzar la próxima jornada.',
};
const DEFAULT_EMAIL_REPLY = {
  id: 'default-email',
  title: 'Respuesta por correo predeterminada',
  bodyText: 'Hola {name},\n\n{agent} respondió:\n\n{message}\n\nPuedes continuar la conversación aquí: {chat_url}',
};
const MESSAGE_PLACEHOLDERS = [
  ['{name}', 'Nombre del cliente'],
  ['{agent}', 'Agente que responde'],
  ['{message}', 'Respuesta enviada'],
  ['{chat_url}', 'Enlace a la conversación'],
];

function IosSelect({ children, className = '', value, onChange, disabled = false, 'aria-label': ariaLabel }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const options = React.Children.toArray(children).map((child) => ({
    key: child.key || String(child.props.value),
    value: child.props.value,
    label: child.props.children,
    disabled: Boolean(child.props.disabled),
  }));
  const selected = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open]);

  function choose(option) {
    if (option.disabled) return;
    onChange?.({ target: { value: option.value } });
    setOpen(false);
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) setOpen(true);
    }
  }

  return <span ref={rootRef} className={`chat-ios-select ${open ? 'is-open' : ''} ${className}`.trim()}>
    <button
      type="button"
      className="chat-ios-select__trigger"
      aria-label={ariaLabel}
      aria-haspopup="listbox"
      aria-expanded={open}
      disabled={disabled}
      onClick={() => setOpen((current) => !current)}
      onKeyDown={handleKeyDown}
    >
      <span>{selected?.label}</span>
      <span className="material-symbols-outlined chat-ios-select__arrow" aria-hidden="true">expand_more</span>
    </button>
    {open ? <span className="chat-ios-select__menu" role="listbox" aria-label={ariaLabel}>
      {options.map((option) => <button
        type="button"
        role="option"
        aria-selected={option.value === value}
        className={option.value === value ? 'is-selected' : ''}
        disabled={option.disabled}
        key={option.key}
        onClick={() => choose(option)}
      >
        <span>{option.label}</span>
        {option.value === value ? <span className="material-symbols-outlined" aria-hidden="true">check</span> : null}
      </button>)}
    </span> : null}
  </span>;
}

function PredefinedMessageSelect({ label, value, defaultReply, replies, onChange, onManage, className = '' }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewRef = useRef(null);
  const selectableReplies = [defaultReply, ...replies.filter((reply) => reply.bodyText !== defaultReply.bodyText)];
  const selectedReply = selectableReplies.find((reply) => reply.bodyText === value);
  const hasLegacyValue = Boolean(value?.trim()) && !selectedReply;

  useEffect(() => {
    if (!previewOpen) return undefined;
    const closeOutside = (event) => {
      if (!previewRef.current?.contains(event.target)) setPreviewOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [previewOpen]);

  return <div className={`chat-message-selector ${className}`.trim()}>
    <div
      ref={previewRef}
      className="chat-message-selector__label"
      onMouseLeave={() => setPreviewOpen(false)}
    >
      <span>{label}</span>
      <button
        type="button"
        className="chat-message-selector__preview-button"
        aria-label={`Ver mensaje seleccionado para ${label}`}
        aria-expanded={previewOpen}
        disabled={!value}
        onClick={() => setPreviewOpen((current) => !current)}
        onMouseEnter={() => {
          if (value) setPreviewOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && previewOpen) {
            event.preventDefault();
            event.stopPropagation();
            setPreviewOpen(false);
          }
        }}
      >
        <span className="material-symbols-outlined" aria-hidden="true">visibility</span>
      </button>
      {previewOpen ? <div className="chat-message-selector__popover" role="status">
        <strong>{selectedReply?.title || 'Mensaje actual'}</strong>
        <p>{value}</p>
      </div> : null}
    </div>
    <IosSelect aria-label={label} value={value || ''} onChange={(event) => onChange(event.target.value)}>
      <option value="" disabled>Seleccionar mensaje predefinido</option>
      {hasLegacyValue ? <option value={value}>Mensaje actual</option> : null}
      {selectableReplies.map((reply) => <option key={reply.id} value={reply.bodyText}>{reply.title}</option>)}
    </IosSelect>
    {!replies.length ? <button className="chat-message-selector__manage" type="button" onClick={onManage}>Crear mensajes predefinidos</button> : null}
  </div>;
}

function headers() {
  const session = JSON.parse(localStorage.getItem('sessionData') || '{}');
  return { Authorization: session.sessionKey || '', Username: session.username || '', 'Content-Type': 'application/json' };
}

export default function WebsiteChatConfig({ onClose, initialBrand = 'tablee' }) {
  const [brand, setBrand] = useState(initialBrand === 'oliva' ? 'oliva' : 'tablee');
  const brandName = brand === 'oliva' ? 'Oliva y Miel' : 'Tablee';
  const currentBrand = useRef(brand);
  currentBrand.current = brand;
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [savedReplies, setSavedReplies] = useState([]);
  const [agentForm, setAgentForm] = useState({ username: '', chatRole: 'agent', isActive: true, receivesPush: true, brands: [brand] });
  const [replyForm, setReplyForm] = useState(EMPTY_REPLY);
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState('visibility');
  const replyBodyRef = useRef(null);

  const closeModal = useCallback(() => {
    if (onClose) onClose();
    else navigate('/app/mensajes');
  }, [navigate, onClose]);

  const closeReplyModal = useCallback(() => {
    setReplyModalOpen(false);
    setReplyForm(EMPTY_REPLY);
  }, []);

  const json = useCallback(async (path, options = {}) => {
    const response = await fetch(apiUrl(path), { ...options, headers: headers() });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) throw new Error(data.message || 'No se pudo completar la acción.');
    return data;
  }, []);

  const load = useCallback(async () => {
    try {
      const [settingsData, agentsData, usersData, repliesData] = await Promise.all([
        json(`/website-chat/settings?brand=${brand}`), json('/website-chat/agents'), json('/website-chat/admin/users'), json(`/website-chat/saved-replies?brand=${brand}`),
      ]);
      if (currentBrand.current !== brand) return;
      setSettings({
        ...settingsData.settings,
        welcomeMessage: settingsData.settings.welcomeMessage?.trim() || DEFAULT_WELCOME_REPLY.bodyText,
        afterHoursMessage: settingsData.settings.afterHoursMessage?.trim() || DEFAULT_AFTER_HOURS_REPLY.bodyText,
        emailBodyTemplate: settingsData.settings.emailBodyTemplate?.trim() || DEFAULT_EMAIL_REPLY.bodyText,
      });
      setAgents(agentsData.agents || []);
      setUsers(usersData.users || []);
      setSavedReplies(repliesData.savedReplies || []);
      setMessage('');
    } catch (error) { if (currentBrand.current === brand) setMessage(error.message); }
  }, [json, brand]);

  useEffect(() => { setSettings(null); setSavedReplies([]); setReplyModalOpen(false); setReplyForm(EMPTY_REPLY); setAgentForm({ username: '', chatRole: 'agent', isActive: true, receivesPush: true, brands: [brand] }); load(); }, [load, brand]);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (replyModalOpen) closeReplyModal();
      else closeModal();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [closeModal, closeReplyModal, replyModalOpen]);

  const eligibleUsers = useMemo(() => users.filter((user) => user.isActive && user.googleEmail), [users]);
  const activeSavedReplies = useMemo(
    () => savedReplies.filter((reply) => reply.isActive && reply.bodyText?.trim()),
    [savedReplies],
  );

  function updateSchedule(day, patch) {
    setSettings((current) => ({
      ...current,
      supportHours: { ...current.supportHours, [day]: { ...current.supportHours[day], ...patch } },
    }));
  }

  async function saveSettings(event) {
    event.preventDefault();
    setBusy(true);
    try {
      await json(`/website-chat/settings?brand=${brand}`, { method: 'PATCH', body: JSON.stringify(settings) });
      closeModal();
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  }

  async function toggleSiteChat(enabled) {
    const previous = settings.enabled;
    setSettings((current) => ({ ...current, enabled }));
    setBusy(true);
    try {
      const data = await json(`/website-chat/settings/site-enabled?brand=${brand}`, {
        method: 'PATCH', body: JSON.stringify({ enabled }),
      });
      setSettings((current) => ({ ...current, enabled: data.enabled }));
      setMessage(data.enabled ? `Chat habilitado en ${brandName}.` : `Chat deshabilitado en ${brandName}.`);
    } catch (error) {
      setSettings((current) => ({ ...current, enabled: previous }));
      setMessage(error.message);
    } finally { setBusy(false); }
  }

  async function toggleWhatsApp(enabled) {
    const previous = settings.whatsappEnabled;
    setSettings((current) => ({ ...current, whatsappEnabled: enabled }));
    setBusy(true);
    try {
      const data = await json(`/website-chat/settings/whatsapp-enabled?brand=${brand}`, {
        method: 'PATCH', body: JSON.stringify({ enabled }),
      });
      setSettings((current) => ({ ...current, whatsappEnabled: data.enabled }));
      setMessage(data.enabled ? `WhatsApp habilitado en ${brandName}.` : `WhatsApp deshabilitado en ${brandName}.`);
    } catch (error) {
      setSettings((current) => ({ ...current, whatsappEnabled: previous }));
      setMessage(error.message);
    } finally { setBusy(false); }
  }

  async function saveAgent(event) {
    event.preventDefault();
    if (!agentForm.username) return;
    setBusy(true);
    try {
      await json(`/website-chat/admin/agents/${encodeURIComponent(agentForm.username)}`, { method: 'PUT', body: JSON.stringify(agentForm) });
      setAgentForm({ username: '', chatRole: 'agent', isActive: true, receivesPush: true, brands: [brand] });
      setMessage('Agente actualizado.');
      await load();
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  }

  async function toggleAgent(agent) {
    setBusy(true);
    try {
      await json(`/website-chat/admin/agents/${encodeURIComponent(agent.username)}`, {
        method: 'PUT',
        body: JSON.stringify({ ...agent, isActive: !agent.isActive }),
      });
      await load();
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  }

  async function saveReply(event) {
    event.preventDefault();
    if (!replyForm.title.trim() || !replyForm.bodyText.trim()) return;
    setBusy(true);
    try {
      const path = `${replyForm.id ? `/website-chat/saved-replies/${replyForm.id}` : '/website-chat/saved-replies'}?brand=${brand}`;
      await json(path, { method: replyForm.id ? 'PUT' : 'POST', body: JSON.stringify(replyForm) });
      closeReplyModal();
      await load();
      setMessage(replyForm.id ? 'Mensaje actualizado.' : 'Mensaje creado.');
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  }

  function createReply() {
    setReplyForm(EMPTY_REPLY);
    setReplyModalOpen(true);
  }

  function editReply(reply) {
    setReplyForm(reply);
    setReplyModalOpen(true);
  }

  function insertReplyPlaceholder(placeholder) {
    const textarea = replyBodyRef.current;
    const start = textarea?.selectionStart ?? replyForm.bodyText.length;
    const end = textarea?.selectionEnd ?? start;
    const nextBodyText = `${replyForm.bodyText.slice(0, start)}${placeholder}${replyForm.bodyText.slice(end)}`;
    setReplyForm((current) => ({ ...current, bodyText: nextBodyText }));
    window.requestAnimationFrame(() => {
      replyBodyRef.current?.focus();
      replyBodyRef.current?.setSelectionRange(start + placeholder.length, start + placeholder.length);
    });
  }

  const tabs = [
    ['visibility', 'Visibilidad', 'visibility'],
    ['schedule', 'Horarios', 'schedule'],
    ['messages', 'Mensajes', 'mail'],
    ['agents', 'Agentes', 'group'],
    ['replies', 'Respuestas', 'quickreply'],
  ];

  return <div className="chat-config-modal-backdrop" onMouseDown={closeModal}>
    <section className="chat-config chat-config--modal" role="dialog" aria-modal="true" aria-labelledby="chat-config-title" onMouseDown={(event) => event.stopPropagation()}>
      <header className="chat-config__header">
        <div><span>{brandName} · Conversaciones</span><h1 id="chat-config-title">Configuración</h1><p>Administra cada bloque desde su sección.</p></div>
        <IosSelect className="chat-config__brand-select" aria-label="Negocio del chat" value={brand} disabled={busy} onChange={(event) => setBrand(event.target.value)}><option value="tablee">Tablee</option><option value="oliva">Oliva y Miel</option></IosSelect>
        <button type="button" className="chat-config__close" onClick={closeModal} aria-label="Cerrar configuración"><span className="material-symbols-outlined">close</span></button>
      </header>

      <nav className="chat-config__tabs" role="tablist" aria-label="Secciones de configuración">
        {tabs.map(([key, label, icon]) => <button type="button" key={key} role="tab" aria-selected={activeTab === key} className={activeTab === key ? 'is-active' : ''} onClick={() => setActiveTab(key)}><span className="material-symbols-outlined">{icon}</span><span>{label}</span></button>)}
      </nav>

      {message ? <div className="chat-config__message" role="status">{message}<button type="button" onClick={() => setMessage('')} aria-label="Cerrar mensaje">×</button></div> : null}

      <div className="chat-config__body">
        {!settings ? <div className="chat-config__loading"><span className="material-symbols-outlined">progress_activity</span><p>{message || 'Cargando configuración…'}</p></div> : null}

        {settings && ['visibility', 'schedule', 'messages'].includes(activeTab) ? <form className={`chat-config__panel ${activeTab === 'messages' ? 'chat-config__panel--messages' : ''}`} onSubmit={saveSettings}>
          {activeTab === 'visibility' ? <>
            <div className="chat-config__panel-title"><div><span>Visibilidad</span><h2>Canales de atención en {brandName}</h2><p>Elige de forma independiente qué botones de contacto pueden ver los clientes en el sitio público.</p></div></div>
            <div className={`chat-site-availability ${settings.enabled ? 'is-enabled' : 'is-disabled'}`}>
              <div className="chat-site-availability__copy">
                <span>Conversación dentro de {brandName}</span>
                <h2>Chat de {brandName}</h2>
                <p>{settings.enabled ? 'Muestra el botón de chat para iniciar y continuar conversaciones sin salir del sitio.' : 'Oculta el chat público; las conversaciones existentes continúan disponibles para el equipo en Kakao Apps.'}</p>
                <small>La bandeja de conversaciones del equipo permanece disponible independientemente de este interruptor.</small>
              </div>
              <label className="chat-site-switch">
                <input type="checkbox" role="switch" aria-label={`Habilitar chat en ${brandName}`} aria-checked={settings.enabled} checked={settings.enabled} disabled={busy} onChange={(event) => toggleSiteChat(event.target.checked)} />
                <span className="chat-site-switch__track" aria-hidden="true"><span /></span>
                <strong>{settings.enabled ? 'Habilitado' : 'Deshabilitado'}</strong>
              </label>
            </div>
            <div className={`chat-site-availability ${settings.whatsappEnabled ? 'is-enabled' : 'is-disabled'}`}>
              <div className="chat-site-availability__copy">
                <span>Contacto externo</span>
                <h2>WhatsApp</h2>
                <p>{settings.whatsappEnabled ? `Muestra un botón independiente que abre una conversación con ${brandName} en WhatsApp.` : 'El botón de WhatsApp permanece oculto para los clientes.'}</p>
                <small>{brand === 'oliva' ? 'Usa el WhatsApp de Oliva y Miel: +56 9 2617 2651.' : 'Usa el número de WhatsApp guardado en Tablee → Configuración general. Si está vacío, utiliza el teléfono general.'}</small>
              </div>
              <label className="chat-site-switch">
                <input type="checkbox" role="switch" aria-label={`Habilitar WhatsApp en ${brandName}`} aria-checked={settings.whatsappEnabled} checked={settings.whatsappEnabled} disabled={busy} onChange={(event) => toggleWhatsApp(event.target.checked)} />
                <span className="chat-site-switch__track" aria-hidden="true"><span /></span>
                <strong>{settings.whatsappEnabled ? 'Habilitado' : 'Deshabilitado'}</strong>
              </label>
            </div>
          </> : null}

          {activeTab === 'schedule' ? <>
            <div className="chat-config__panel-title"><div><span>Disponibilidad</span><h2>Horario de atención</h2><p>Define cuándo el equipo aparece disponible para responder.</p></div></div>
            <label>Zona horaria<input value={settings.supportTimezone} onChange={(event) => setSettings({ ...settings, supportTimezone: event.target.value })} /></label>
            <div className="chat-schedule">{DAYS.map(([day, label]) => <div key={day} className={!settings.supportHours[day]?.enabled ? 'is-disabled' : ''}>
              <label className="chat-schedule__day">
                <input type="checkbox" role="switch" aria-label={`Habilitar atención el ${label}`} aria-checked={settings.supportHours[day]?.enabled || false} checked={settings.supportHours[day]?.enabled || false} onChange={(event) => updateSchedule(day, { enabled: event.target.checked })} />
                <span className="chat-schedule-switch__track" aria-hidden="true"><span /></span>
                <span>{label}</span>
              </label>
              <label className="chat-schedule__time"><span>Desde</span><input aria-label={`Hora de inicio ${label}`} type="time" value={settings.supportHours[day]?.start || '09:00'} disabled={!settings.supportHours[day]?.enabled} onChange={(event) => updateSchedule(day, { start: event.target.value })} /></label>
              <span className="chat-schedule__separator" aria-hidden="true">—</span>
              <label className="chat-schedule__time"><span>Hasta</span><input aria-label={`Hora de término ${label}`} type="time" value={settings.supportHours[day]?.end || '18:00'} disabled={!settings.supportHours[day]?.enabled} onChange={(event) => updateSchedule(day, { end: event.target.value })} /></label>
            </div>)}</div>
          </> : null}

          {activeTab === 'messages' ? <>
            <div className="chat-config__panel-title"><div><span>Mensajes</span><h2>Contenido y notificaciones</h2><p>Personaliza la experiencia del cliente y el seguimiento del equipo.</p></div></div>
            <div className="chat-message-selectors">
              <PredefinedMessageSelect
                label="Mensaje de bienvenida"
                value={settings.welcomeMessage}
                defaultReply={DEFAULT_WELCOME_REPLY}
                replies={activeSavedReplies}
                onChange={(welcomeMessage) => setSettings({ ...settings, welcomeMessage })}
                onManage={() => setActiveTab('replies')}
              />
              <PredefinedMessageSelect
                label="Mensaje fuera de horario"
                value={settings.afterHoursMessage}
                defaultReply={DEFAULT_AFTER_HOURS_REPLY}
                replies={activeSavedReplies}
                onChange={(afterHoursMessage) => setSettings({ ...settings, afterHoursMessage })}
                onManage={() => setActiveTab('replies')}
              />
            </div>
            <div className="chat-messages-lower">
              <div className="chat-config__grid chat-message-metrics">
                <label>Escalar sin asignar después de<input type="number" min="1" max="1440" value={settings.unassignedEscalationMinutes} onChange={(event) => setSettings({ ...settings, unassignedEscalationMinutes: Number(event.target.value) })} /><small>minutos en horario</small></label>
                <label>Retener conversaciones<input type="number" min="1" max="120" value={settings.retentionMonths} onChange={(event) => setSettings({ ...settings, retentionMonths: Number(event.target.value) })} /><small>meses tras el cierre</small></label>
              </div>
              <div className="chat-config__subsection">
                <div className="chat-config__panel-title">
                  <div><span>Correo</span><h2>Respuesta al cliente</h2></div>
                  <label className="chat-email-switch">
                    <input type="checkbox" role="switch" aria-label="Habilitar respuesta automática por correo" aria-checked={settings.emailReplyEnabled} checked={settings.emailReplyEnabled} onChange={(event) => setSettings({ ...settings, emailReplyEnabled: event.target.checked })} />
                    <span className="chat-email-switch__track" aria-hidden="true"><span /></span>
                    <strong>{settings.emailReplyEnabled ? 'Habilitado' : 'Deshabilitado'}</strong>
                  </label>
                </div>
                <div className="chat-email-fields">
                  <label>Asunto<input value={settings.emailSubjectTemplate} onChange={(event) => setSettings({ ...settings, emailSubjectTemplate: event.target.value })} /></label>
                  <PredefinedMessageSelect
                    className="chat-email-message-selector"
                    label="Mensaje del correo"
                    value={settings.emailBodyTemplate}
                    defaultReply={DEFAULT_EMAIL_REPLY}
                    replies={activeSavedReplies}
                    onChange={(emailBodyTemplate) => setSettings({ ...settings, emailBodyTemplate })}
                    onManage={() => setActiveTab('replies')}
                  />
                </div>
              </div>
            </div>
          </> : null}
          <footer><button type="submit" disabled={busy}>Guardar</button></footer>
        </form> : null}

        {settings && activeTab === 'agents' ? <section className="chat-config__panel">
          <div className="chat-config__panel-title"><div><span>Acceso</span><h2>Agentes</h2><p>Solo usuarios OVM activos con correo Google pueden ingresar.</p></div></div>
          <form className="chat-agent-form" onSubmit={saveAgent}>
            <IosSelect required aria-label="Seleccionar usuario OVM" value={agentForm.username} onChange={(event) => {
              const username = event.target.value;
              const existing = agents.find((agent) => agent.username === username);
              setAgentForm(existing ? { username, chatRole: existing.chatRole, isActive: existing.isActive, receivesPush: existing.receivesPush, brands: existing.brands || [brand] } : { username, chatRole: 'agent', isActive: true, receivesPush: true, brands: [brand] });
            }}><option value="">Seleccionar usuario OVM</option>{eligibleUsers.map((user) => <option key={user.username} value={user.username}>{user.name || user.username} · {user.googleEmail}</option>)}</IosSelect>
            <IosSelect aria-label="Rol del agente" value={agentForm.chatRole} onChange={(event) => setAgentForm({ ...agentForm, chatRole: event.target.value })}><option value="agent">Agente</option><option value="admin">Administrador de chat</option></IosSelect>
            <label className="chat-agent-push-switch">
              <input type="checkbox" role="switch" aria-label="Recibir notificaciones push" aria-checked={agentForm.receivesPush} checked={agentForm.receivesPush} onChange={(event) => setAgentForm({ ...agentForm, receivesPush: event.target.checked })} />
              <span className="chat-agent-push-switch__track" aria-hidden="true"><span /></span>
              <strong>{agentForm.receivesPush ? 'Push activo' : 'Push inactivo'}</strong>
            </label>
            <fieldset className="chat-agent-brands"><legend>Marcas con acceso a conversaciones y notificaciones</legend>{[['tablee', 'Tablee'], ['oliva', 'Oliva y Miel']].map(([key, name]) => <label key={key}><input type="checkbox" checked={agentForm.brands.includes(key)} onChange={(event) => setAgentForm({ ...agentForm, brands: event.target.checked ? [...new Set([...agentForm.brands, key])] : agentForm.brands.filter((value) => value !== key) })} />{name}</label>)}</fieldset>
            <button type="submit" disabled={busy || !agentForm.brands.length}>Agregar o actualizar</button>
          </form>
          <div className="chat-config__table"><div className="chat-config__table-head"><span>Agente</span><span>Google</span><span>Rol</span><span>Push</span><span>Acción</span></div>{agents.map((agent) => <div key={agent.username}><strong>{agent.name || agent.username}<small>{agent.username} · {(agent.brands || []).map((key) => key === 'oliva' ? 'Oliva y Miel' : 'Tablee').join(' / ')}</small></strong><span>{agent.googleEmail || 'Sin correo'}</span><span>{agent.chatRole}</span><span>{agent.receivesPush ? 'Sí' : 'No'}</span><button type="button" onClick={() => toggleAgent(agent)} disabled={busy}>{agent.isActive ? 'Desactivar' : 'Activar'}</button></div>)}</div>
        </section> : null}

        {settings && activeTab === 'replies' ? <section className="chat-config__panel chat-config__panel--replies">
          <div className="chat-config__panel-title">
            <div><span>Contenido</span><h2>Mensajes predefinidos</h2><p>Reutilízalos en respuestas rápidas, mensajes automáticos y correos.</p></div>
            <button type="button" className="chat-replies-add" onClick={createReply}><span className="material-symbols-outlined">add</span>Agregar mensaje</button>
          </div>
          {savedReplies.length ? <div className="chat-replies-table-wrap">
            <table className="chat-replies-table">
              <thead><tr><th>Nombre</th><th>Contenido</th><th>Orden</th><th>Estado</th><th><span className="sr-only">Acciones</span></th></tr></thead>
              <tbody>{savedReplies.map((reply) => <tr key={reply.id}>
                <td><strong>{reply.title}</strong></td>
                <td><span className="chat-replies-table__message">{reply.bodyText}</span></td>
                <td>{reply.sortOrder}</td>
                <td><span className={`chat-replies-table__status ${reply.isActive ? 'is-active' : ''}`}>{reply.isActive ? 'Activa' : 'Inactiva'}</span></td>
                <td><button type="button" className="chat-replies-table__edit" onClick={() => editReply(reply)} aria-label={`Editar ${reply.title}`} title="Editar"><span className="material-symbols-outlined" aria-hidden="true">edit</span></button></td>
              </tr>)}</tbody>
            </table>
          </div> : <div className="chat-saved-library__empty"><span className="material-symbols-outlined">forum</span><strong>Aún no hay mensajes</strong><p>Agrega el primero para usarlo en el chat y en los mensajes automáticos.</p><button type="button" onClick={createReply}>Agregar mensaje</button></div>}
        </section> : null}
      </div>

      {replyModalOpen ? <div className="chat-reply-modal-backdrop" onMouseDown={closeReplyModal}>
        <section className="chat-reply-modal" role="dialog" aria-modal="true" aria-labelledby="chat-reply-modal-title" onMouseDown={(event) => event.stopPropagation()}>
          <header>
            <div><span>{replyForm.id ? 'Editar mensaje' : 'Nuevo mensaje'}</span><h2 id="chat-reply-modal-title">{replyForm.id ? replyForm.title || 'Mensaje predefinido' : 'Agregar mensaje predefinido'}</h2><p>Este contenido podrá utilizarse en chat, mensajes automáticos y correos.</p></div>
            <button type="button" onClick={closeReplyModal} aria-label="Cerrar editor de mensaje"><span className="material-symbols-outlined">close</span></button>
          </header>
          <form className="chat-reply-modal__form" onSubmit={saveReply}>
            <div className="chat-reply-modal__fields">
              <label>Nombre
                <input required autoFocus placeholder="Ej. Saludo inicial" value={replyForm.title} onChange={(event) => setReplyForm({ ...replyForm, title: event.target.value })} />
              </label>
              <label>Contenido
                <textarea ref={replyBodyRef} required placeholder="Escribe el mensaje completo…" value={replyForm.bodyText} onChange={(event) => setReplyForm({ ...replyForm, bodyText: event.target.value })} />
              </label>
              <div className="chat-reply-placeholders">
                <div><strong>Insertar variable</strong><small>Se reemplazará automáticamente al enviar el correo.</small></div>
                <div>{MESSAGE_PLACEHOLDERS.map(([placeholder, description]) => <button type="button" key={placeholder} title={description} onClick={() => insertReplyPlaceholder(placeholder)}>{placeholder}</button>)}</div>
              </div>
              <div className="chat-reply-form__options">
                <label>Orden
                  <input type="number" min="0" value={replyForm.sortOrder} onChange={(event) => setReplyForm({ ...replyForm, sortOrder: Number(event.target.value) })} />
                </label>
                <label className="chat-reply-status-switch">
                  <input type="checkbox" role="switch" checked={replyForm.isActive} onChange={(event) => setReplyForm({ ...replyForm, isActive: event.target.checked })} />
                  <span className="chat-reply-status-switch__track" aria-hidden="true"><span /></span>
                  <strong>{replyForm.isActive ? 'Activa' : 'Inactiva'}</strong>
                </label>
              </div>
            </div>
            <footer><button type="button" onClick={closeReplyModal}>Cancelar</button><button type="submit" disabled={busy}>{replyForm.id ? 'Guardar cambios' : 'Agregar mensaje'}</button></footer>
          </form>
        </section>
      </div> : null}
    </section>
  </div>;
}
